import React, { useEffect, useState } from "react";
import { Users, Loader2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useCreateGroup } from "../../hooks/useFamily";

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [groupName, setGroupName] = useState("");
  const createGroupMutation = useCreateGroup();

  const handleClose = () => {
    setGroupName("");
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleCreate = async () => {
    if (!groupName.trim()) return;

    try {
      await createGroupMutation.mutateAsync(groupName.trim());
      setGroupName("");
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error creating group:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center px-4"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={handleClose}
        aria-hidden
      />

      <div
        className="relative z-10 mx-auto w-full max-w-[400px] duration-200 animate-in zoom-in-95 fade-in-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-group-title"
      >
        <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-2xl ring-1 ring-border/80">
          <div className="flex items-start justify-between gap-3 p-4 pb-0 sm:p-6">
            <div className="flex items-center gap-2 text-base sm:text-lg font-semibold" id="create-group-title">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-purple-600">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              Create Family Group
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3 sm:space-y-4 p-4 sm:px-6">
            <div className="space-y-2">
              <Label htmlFor="groupName" className="text-xs sm:text-sm font-semibold">
                Group Name
              </Label>
              <Input
                id="groupName"
                placeholder="e.g., My Family, Friends, Workout Buddies"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && groupName.trim()) {
                    handleCreate();
                  }
                }}
                className="h-10 sm:h-11 text-sm sm:text-base"
                maxLength={50}
                autoFocus
              />
              <p className="text-compact-xs sm:text-xs text-muted-foreground">
                Choose a name that describes your health tracking circle.
              </p>
            </div>

            <div className="bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800 rounded-lg p-2.5 sm:p-3">
              <p className="text-compact-xs sm:text-xs text-violet-800 dark:text-violet-200">
                <strong className="font-semibold">💡 Tip:</strong> After creating your group, you can invite family members or friends to join and track health together!
              </p>
            </div>
          </div>

          <div className="flex gap-2 p-4 sm:px-6 sm:pb-6">
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1 h-10 sm:h-11 text-sm"
              disabled={createGroupMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!groupName.trim() || createGroupMutation.isPending}
              className="flex-1 h-10 sm:h-11 text-sm bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            >
              {createGroupMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
                  <span className="text-xs sm:text-sm">Creating...</span>
                </>
              ) : (
                <>
                  <Users className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Create Group</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;
